import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  AppointmentHistoryRowProps,
  appointmentRenderInfo,
} from '../../interfaces';
import { useAppointmentHasuraStatus } from '../upcoming-appointments/helper/getStatusClassName';
import { twMerge } from 'tailwind-merge';
import ButtonComponent from 'components/button/buttonComponent';
import { AppointmentHasuraStatus } from '../../const/appointment';
import { ReactComponent as Doc } from 'assets/icons/file.svg';
import { FileOutput } from 'graphql/generated/hasura';
import TestKitButtonBlock from './TestKitButtonBlock';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';
import { formatDateForAppointment } from '../../../../utilities/functions';

interface IItemRow {
  label?: string;
  children: ReactNode;
}

type ActionType = {
  onClick: (id: string) => void;
  text?: string;
};

const ItemRow: React.FC<IItemRow> = ({ label, children }) => {
  return (
    <div className="flex items-baseline gap-2 justify-between text-sm text-med-gray-3">
      <div className="text-charcoal-gray font-semibold">{label}</div>
      <div className="text-right">{children}</div>
    </div>
  );
};

const PastAppointmentMobileView: React.FC<AppointmentHistoryRowProps> = ({
  date,
  type,
  locale: localeProp,
  appointmentStatus,
  subjectOfAppointment,
  actions,
  id,
  diagnosticReports,
  showReason = false,
  summaryCompleted,
}) => {
  const Icon = appointmentRenderInfo[type].icon;
  const { user } = useAuth();

  const isProvider = useMemo(() => user?.role === Roles.PROVIDER, [user]);
  const { statusClassName, label } = useAppointmentHasuraStatus(
    appointmentStatus as AppointmentHasuraStatus,
  );

  const patientReports = (diagnosticReports ?? [])
    .map(({ patientPdf }) => patientPdf)
    .filter(Boolean);
  const providerReports = (diagnosticReports ?? [])
    .map(({ providerPdf }) => providerPdf)
    .filter(Boolean);

  const Actions = useCallback(() => {
    if (!Array.isArray(actions)) return null;
    if (!isProvider) {
      const { onClick: onClickSummary } = (actions[0] ||
        {}) as unknown as ActionType;
      const { onClick: onClickTestKit } = (actions[1] ||
        {}) as unknown as ActionType;
      const { onClick: onClickDetails } = (actions[2] ||
        {}) as unknown as ActionType;

      return (
        <div className="flex items-center flex-col gap-2.5">
          {(appointmentStatus as unknown as AppointmentHasuraStatus) ===
          AppointmentHasuraStatus.Completed ? (
            <ButtonComponent
              type="underline"
              className="!text-xs !font-bold"
              containerClassName="!gap-1"
              onClick={() => onClickSummary(id as string)}
              Icon={Doc}
              iconPosition="left"
              iconWidth="w-[1.25rem] !stroke-transparent"
              testID={`summary-${id}-button`}
            >
              Summary
            </ButtonComponent>
          ) : (
            <ButtonComponent
              type="underline"
              className="!text-xs !font-bold"
              onClick={() => onClickDetails(id as string)}
              testID={`details-${id}-button`}
            >
              Details
            </ButtonComponent>
          )}

          {!!patientReports.length && (
            <TestKitButtonBlock
              diagnosticReports={patientReports as FileOutput[]}
              onClickTestKit={onClickTestKit}
              appointmentId={''}
            />
          )}
        </div>
      );
    }
    const { onClick: onClickSummary } = actions[0] as unknown as ActionType;
    const { onClick: onClickCompleteSummary } =
      actions[1] as unknown as ActionType;
    const { onClick: onTestKitClick } = actions[2] as unknown as ActionType;

    const isShowSummaryAction =
      (appointmentStatus as unknown as AppointmentHasuraStatus) ===
      AppointmentHasuraStatus.Completed;

    const isPatientNotShowingUp =
      (appointmentStatus as unknown as AppointmentHasuraStatus) ===
      AppointmentHasuraStatus.PatientNotShowingUp;

    return (
      <div className="flex items-center flex-col gap-2.5">
        {(summaryCompleted || isPatientNotShowingUp) && (
          <ButtonComponent
            type="underline"
            className="!text-xs !font-bold"
            containerClassName="!gap-1"
            onClick={() => onClickSummary(id as string)}
            Icon={Doc}
            iconPosition="left"
            iconWidth="w-[1.25rem] !stroke-transparent"
            testID={`summary-${id}-button`}
          >
            Summary
          </ButtonComponent>
        )}

        {isShowSummaryAction && !summaryCompleted && (
          <ButtonComponent
            className="!text-xs !font-bold"
            onClick={() => onClickCompleteSummary(id as string)}
            Icon={Doc}
            iconPosition="left"
            iconWidth="w-[1.25rem] !stroke-transparent !fill-current"
            testID={`completeSummary-${id}-button`}
          >
            Complete Summary
          </ButtonComponent>
        )}
        {!!providerReports.length && (
          <TestKitButtonBlock
            diagnosticReports={providerReports as FileOutput[]}
            onClickTestKit={onTestKitClick}
            appointmentId={''}
          />
        )}
      </div>
    );
  }, [
    actions,
    appointmentStatus,
    id,
    isProvider,
    patientReports,
    providerReports,
    summaryCompleted,
  ]);
  return (
    <div className="flex flex-col gap-2 border border-transparent border-b-gray-opacity-15 pb-4">
      <ItemRow label={localeProp?.date || 'date'}>
        {formatDateForAppointment(new Date(date as string), {
          simplified: true,
          withTimeZone: true,
        })}
      </ItemRow>
      <ItemRow label={localeProp?.type || 'type'}>
        <div className="flex items-center">
          <Icon className="inline" />
          <span className="ml-2 capitalize">
            {type.toString().toLowerCase()}
          </span>
        </div>
      </ItemRow>
      {showReason && (
        <ItemRow label={localeProp?.reason || 'Reason'}>
          <div className="flex items-center">{subjectOfAppointment}</div>
        </ItemRow>
      )}
      <ItemRow label={localeProp?.status}>
        <span className={twMerge('capitalize', statusClassName)}>{label}</span>
      </ItemRow>

      <Actions />
    </div>
  );
};

export default PastAppointmentMobileView;
